import React, { useState, useEffect, useMemo } from 'react';
import { Check, AlertTriangle } from 'lucide-react';

interface EnumOption {
    key: string;
    label: string;
}

interface EnumPickerModalProps {
    show: boolean;
    onHide: () => void;
    onSave: (value: string | string[]) => Promise<void>;
    title: string;
    options: EnumOption[];
    currentValue: string | string[] | null;
    multiSelect?: boolean;
    warning?: string;  // Optional warning message (e.g., for category change)
}

// Brand colors from index.css
const BRAND_DARK_BLUE = '#001D60';

/**
 * Generic modal for picking enum/list values.
 * Supports single select (radio-style) and multi-select (checkbox-style).
 * Options are sorted alphabetically by label.
 */
export const EnumPickerModal: React.FC<EnumPickerModalProps> = ({
    show,
    onHide,
    onSave,
    title,
    options,
    currentValue,
    multiSelect = false,
    warning
}) => {
    const [selectedValue, setSelectedValue] = useState<string | null>(null);
    const [selectedValues, setSelectedValues] = useState<string[]>([]);
    const [saving, setSaving] = useState(false);
    const [showWarningConfirm, setShowWarningConfirm] = useState(false);
    const [pendingValue, setPendingValue] = useState<string | string[] | null>(null);

    // Sort options alphabetically by label using Swedish locale
    const sortedOptions = useMemo(() => {
        return [...options].sort((a, b) => a.label.localeCompare(b.label, 'sv'));
    }, [options]);

    // Initialize selection state when modal opens
    useEffect(() => {
        if (show) {
            if (multiSelect) {
                setSelectedValues(Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : []);
            } else {
                setSelectedValue(typeof currentValue === 'string' ? currentValue : null);
            }
            setShowWarningConfirm(false);
            setPendingValue(null);
        }
    }, [show, currentValue, multiSelect]);

    if (!show) return null;

    const handleSelect = async (key: string) => {
        if (saving) return;

        if (multiSelect) {
            // Toggle selection for multi-select
            setSelectedValues(prev =>
                prev.includes(key)
                    ? prev.filter(k => k !== key)
                    : [...prev, key]
            );
        } else {
            // Single select - check for warning
            if (warning && key !== currentValue) {
                setPendingValue(key);
                setShowWarningConfirm(true);
            } else {
                await saveValue(key);
            }
        }
    };

    const handleMultiSelectSave = async () => {
        if (saving) return;

        if (warning) {
            setPendingValue(selectedValues);
            setShowWarningConfirm(true);
        } else {
            await saveValue(selectedValues);
        }
    };

    const handleWarningConfirm = async () => {
        if (pendingValue !== null) {
            await saveValue(pendingValue);
        }
    };

    const saveValue = async (value: string | string[]) => {
        setSaving(true);
        try {
            await onSave(value);
            onHide();
        } catch (error) {
            console.error('Error saving value:', error);
            alert('Ett fel uppstod vid sparande. Försök igen.');
        } finally {
            setSaving(false);
            setShowWarningConfirm(false);
            setPendingValue(null);
        }
    };

    const isSelected = (key: string): boolean => {
        if (multiSelect) {
            return selectedValues.includes(key);
        }
        return selectedValue === key || currentValue === key;
    };

    return (
        <>
            <div
                className="modal show d-block"
                style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
                onMouseDown={(e) => {
                    if (e.target === e.currentTarget && !saving) {
                        onHide();
                    }
                }}
            >
                <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">{title}</h5>
                            <button
                                type="button"
                                className="btn-close"
                                onClick={onHide}
                                aria-label="Stäng"
                                disabled={saving}
                            />
                        </div>
                        <div className="modal-body">
                            {showWarningConfirm ? (
                                <div>
                                    <div className="alert alert-warning d-flex align-items-start">
                                        <AlertTriangle size={20} className="me-2 flex-shrink-0 mt-1" />
                                        <div>{warning}</div>
                                    </div>
                                    <div className="d-flex gap-2 mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-warning"
                                            onClick={handleWarningConfirm}
                                            disabled={saving}
                                        >
                                            {saving ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Sparar...
                                                </>
                                            ) : (
                                                'Fortsätt ändå'
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-secondary"
                                            onClick={() => {
                                                setShowWarningConfirm(false);
                                                setPendingValue(null);
                                            }}
                                            disabled={saving}
                                        >
                                            Avbryt
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {sortedOptions.length === 0 ? (
                                        <div className="alert alert-warning">
                                            <p className="mb-0">Inga alternativ tillgängliga</p>
                                        </div>
                                    ) : (
                                        <div className="list-group" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                            {sortedOptions.map((option) => {
                                                const selected = isSelected(option.key);

                                                return (
                                                    <button
                                                        key={option.key}
                                                        type="button"
                                                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                                                        onClick={() => handleSelect(option.key)}
                                                        disabled={saving}
                                                        style={{
                                                            cursor: saving ? 'not-allowed' : 'pointer',
                                                            padding: '12px 16px',
                                                            backgroundColor: selected ? BRAND_DARK_BLUE : undefined,
                                                            borderColor: selected ? BRAND_DARK_BLUE : undefined,
                                                            color: selected ? '#fff' : undefined,
                                                        }}
                                                    >
                                                        <span>{option.label}</span>
                                                        {selected && <Check size={18} />}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className="modal-footer">
                            {multiSelect && !showWarningConfirm && (
                                <button
                                    type="button"
                                    className="btn btn-primary me-auto"
                                    onClick={handleMultiSelectSave}
                                    disabled={saving || selectedValues.length === 0}
                                >
                                    {saving ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Sparar...
                                        </>
                                    ) : (
                                        `Spara (${selectedValues.length} valda)`
                                    )}
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={onHide}
                                disabled={saving}
                            >
                                {showWarningConfirm ? 'Avbryt' : 'Stäng'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-backdrop fade show"></div>
        </>
    );
};
